import {
    PARKING_TYPE_AIRPORT,
    PARKING_PROVIDER_AENA,
    PARKING_SERVICE_CAR_VEHICLE,
    PARKING_SERVICE_TRUCK_VEHICLE,
    PARKING_SERVICE_MOTORCYCLE_VEHICLE,
    PARKING_SERVICE_VANS_VEHICLE,
    PARKING_SERVICE_CARAVANS_VEHICLE,
    PARKING_SERVICE_BUS_VEHICLE,
    PARKING_SERVICE_BICYCLE_VEHICLE,
    PARKING_SERVICE_OPEN24,
    PARKING_SERVICE_CAR_CHARGE_POINT,
    PARKING_SERVICE_DISABLED_ACCESS,
    PARKING_BOOKING_SERVICE_TYPE_AIRPORT_TRANSFER,
    PARKING_BOOKING_SERVICE_TYPE_PICK_UP,
    PARKING_BOOKING_SERVICE_TYPE_WALKING,
    AVAILABLE,
} from 'commons/js/constants'

const combineFilters = (...filters) => (arg) => filters.reduce((result, filter) => result && filter(arg), true)

const always = (val) => () => val

const buildFilter = (filters) => ({ condition, filter }) => condition(filters) ? filter : always(true)

const nonCountableFilters = ['parkingType.airport']

export const getActiveFilters = (filters) =>
    Object.keys(filters).reduce(
        (totalResult, filterType) =>
            totalResult + Object.keys(filters[filterType]).reduce(
                (result, filterName) =>
                    filters[filterType][filterName] &&
                    !nonCountableFilters.includes(`${filterType}.${filterName}`)
                        ? result + 1
                        : result
            , 0)
        , 0)

// Conditions

export function isAirportParkingTypeFilterEnabled (filters) {
    return filters && filters.parkingType && filters.parkingType.airport
}

export function isAenaParkingProviderFilterEnabled (filters) {
    return filters && filters.parkingProvider && filters.parkingProvider.aena
}

export function isCarParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.car
}

export function isMotorcycleParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.motorcycle
}

export function isVansParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.vans
}

export function isCaravansParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.caravans
}

export function isBusParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.bus
}

export function isBicycleParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.bicycle
}

export function isTruckParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.truck
}

export function isOpen24ParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.open24
}

export function isCarChargePointParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.carChargePoint
}

export function isDisabledAccessParkingServiceFilterEnabled (filters) {
    return filters && filters.parkingService && filters.parkingService.disabledAccess
}

export function isAirportTransferBookingServiceEnabled (filters) {
    return filters && filters.bookingService && filters.bookingService.airportTransfer
}

export function isPickUpBookingServiceEnabled (filters) {
    return filters && filters.bookingService && filters.bookingService.pickUp
}

export function isWalkingBookingServiceEnabled (filters) {
    return filters && filters.bookingService && filters.bookingService.walking
}

// Filters

function hasParkingType (parkingType) {
    return (parking) => parking.typeString === parkingType
}

function hasParkingProvider (parkingProvider) {
    return (parking) => parking.provider === parkingProvider
}

function hasSlug (slug) {
    return (vehicleService) => vehicleService.slug === slug
}

function hasParkingService (slug) {
    return (parking) =>
        parking.services.some(hasSlug(slug)) ||
        parking.services_additional.some(hasSlug(slug)) ||
        parking.services_other.some(hasSlug(slug)) ||
        parking.services_vehicles.some(hasSlug(slug))
}

function hasType (type) {
    return (bookingService) => bookingService.type === type
}

function hasBookingService (bookingServiceType) {
    return (parking) =>
        parking.bookingServices.some(hasType(bookingServiceType))
}

const availableParkingFilters = [
    {
        condition: isAirportParkingTypeFilterEnabled,
        filter: hasParkingType(PARKING_TYPE_AIRPORT),
    },
    {
        condition: isAenaParkingProviderFilterEnabled,
        filter: hasParkingProvider(PARKING_PROVIDER_AENA),
    },
    {
        condition: isCarParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_CAR_VEHICLE),
    },
    {
        condition: isMotorcycleParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_MOTORCYCLE_VEHICLE),
    },
    {
        condition: isVansParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_VANS_VEHICLE),
    },
    {
        condition: isCaravansParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_CARAVANS_VEHICLE),
    },
    {
        condition: isBusParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_BUS_VEHICLE),
    },
    {
        condition: isBicycleParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_BICYCLE_VEHICLE),
    },
    {
        condition: isOpen24ParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_OPEN24),
    },
    {
        condition: isCarChargePointParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_CAR_CHARGE_POINT),
    },
    {
        condition: isDisabledAccessParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_DISABLED_ACCESS),
    },
    {
        condition: isAirportTransferBookingServiceEnabled,
        filter: hasBookingService(PARKING_BOOKING_SERVICE_TYPE_AIRPORT_TRANSFER),
    },
    {
        condition: isPickUpBookingServiceEnabled,
        filter: hasBookingService(PARKING_BOOKING_SERVICE_TYPE_PICK_UP),
    },
    {
        condition: isWalkingBookingServiceEnabled,
        filter: hasBookingService(PARKING_BOOKING_SERVICE_TYPE_WALKING),
    },
    {
        condition: isTruckParkingServiceFilterEnabled,
        filter: hasParkingService(PARKING_SERVICE_TRUCK_VEHICLE),
    },
]

export const makeParkingsFilter = (filters) =>
    combineFilters(...availableParkingFilters.map(buildFilter(filters)))

export const getDefaultFilters = (initialFilters = {}) => updateFilters({
    parkingType: {
        airport: false,
    },
    parkingProvider: {
        aena: false,
    },
    parkingService: {
        car: false,
        motorcycle: false,
        vans: false,
        caravans: false,
        bus: false,
        bicycle: false,
        truck: false,
        open24: false,
        carChargePoint: false,
        disabledAccess: false,
    },
    bookingService: {
        airportTransfer: false,
        pickUp: false,
        walking: false,
    },
}, initialFilters)

const resetAirportFilters = (filters) => {
    if (!filters.parkingType.airport) {
        return {
            ...filters,
            parkingProvider: {
                aena: false,
            },
            bookingService: {
                airportTransfer: false,
                pickUp: false,
                walking: false,
            },
        }
    }
    return filters
}

export const updateFiltersWithRestrictions = (prevFilters, filters) => {
    return resetAirportFilters(updateFilters(prevFilters, filters))
}
export const updateFilters = (prevFilters, filters) => ({
    parkingType: {
        ...prevFilters.parkingType,
        ...filters.parkingType,
    },
    parkingProvider: {
        ...prevFilters.parkingProvider,
        ...filters.parkingProvider,
    },
    parkingService: {
        ...prevFilters.parkingService,
        ...filters.parkingService,
    },
    bookingService: {
        ...prevFilters.bookingService,
        ...filters.bookingService,
    },
})

export const getAvailableParkings = (parkings) => {
    return parkings.filter(({bookingAvailability}) => !!bookingAvailability && (bookingAvailability.availabilityStatus === AVAILABLE))
}

export const getUnavailableParkings = (parkings) => {
    return parkings.filter(({bookingAvailability}) => !!bookingAvailability && (bookingAvailability.availabilityStatus !== AVAILABLE))
}
